import type { StoreApi } from 'zustand';
import { createStore } from 'zustand';

import type { JobProfileComponent } from '~/shared/data-contract/job-profile';

import type { JobItemDetails } from '../api/use-job-item-details';

export type DraftVersion = JobItemDetails;

export type DraftVersionState = {
    readonly drafts: ReadonlyMap<string, DraftVersion>;
};

export type DraftVersionActions = {
    readonly addDraft: (draft: DraftVersion) => void;
    readonly getDraft: (itemId: string) => DraftVersion | undefined;
    readonly hasDraft: (itemId: string) => boolean;
    readonly removeDraft: (itemId: string) => void;
    readonly updateDraft: (itemId: string, draft: JobProfileComponent) => void;
};

export type DraftVersionStore = DraftVersionActions & DraftVersionState;

export type DraftVersionStoreDependencies = Record<string, never>;

export const createDraftVersionStore = (
    _dependencies: DraftVersionStoreDependencies = {},
): StoreApi<DraftVersionStore> => {
    return createStore<DraftVersionStore>((set, get) => ({
        addDraft: (draft) => {
            set((state) => {
                const newDrafts = new Map(state.drafts);
                newDrafts.set(draft.id, draft);

                return { drafts: newDrafts };
            });
        },
        drafts: new Map(),

        getDraft: (itemId) => {
            return get().drafts.get(itemId);
        },

        hasDraft: (itemId) => {
            return get().drafts.has(itemId);
        },

        removeDraft: (itemId) => {
            set((state) => {
                const newDrafts = new Map(state.drafts);
                newDrafts.delete(itemId);

                return { drafts: newDrafts };
            });
        },

        updateDraft: (itemId, updatedDraft) => {
            const draft = get().drafts.get(itemId);
            if (draft !== undefined) {
                set((state) => {
                    const newDrafts = new Map(state.drafts);
                    const updatedJobProfile =
                        draft.jobProfile !== undefined
                            ? { ...draft.jobProfile, ...updatedDraft }
                            : { ...updatedDraft, state: 'draft' as const };
                    newDrafts.set(itemId, { ...draft, jobProfile: updatedJobProfile });

                    return { drafts: newDrafts };
                });
            }
        },
    }));
};
